/** The token rows: expiry flagged when past, row actions Extend (personal tokens only) then Revoke. */

import { CalendarPlus, KeyRound, Trash2 } from 'lucide-react';

import type { McpToken } from '@/api/types';
import { formatDateTime, formatUtcDate } from '@/ui/format';
import { SortHeader } from '@/ui/SortHeader';
import type { SortOrder } from '@/ui/sortRows';
import { StateCard } from '@/ui/StateCard';

import { scopeLabel } from './mcpScopes';

function TokenRow({ token, onExtend, onRevoke }: { token: McpToken; onExtend: () => void; onRevoke: () => void }) {
  // A connection ends when its sign-in does; the AI tool signs in again rather than being extended.
  const connection = token.kind === 'connection';
  return (
    <tr>
      <th scope="row">
        {token.name}
        {connection && <> <span className="badge badge-info">Signed in</span></>}
      </th>
      <td>
        <span className="scope-list">
          {token.scopes.map((scope) => (
            <span key={scope} className="badge badge-neutral">
              {scopeLabel(scope)}
            </span>
          ))}
        </span>
      </td>
      <td>
        {formatUtcDate(token.expires_at)} UTC {token.expired && <span className="badge badge-neutral">Expired</span>}
      </td>
      <td>{formatDateTime(token.last_used_at) ?? 'Never used'}</td>
      <td>{formatDateTime(token.created_at)}</td>
      <td className="row-actions-cell">
        {!connection && (
          <button type="button" className="btn btn-secondary btn-icon" data-tooltip="Extend" aria-label={`Extend ${token.name}`} onClick={onExtend}>
            <CalendarPlus aria-hidden="true" />
          </button>
        )}
        <button type="button" className="btn btn-secondary btn-icon" data-tooltip="Revoke" aria-label={`Revoke ${token.name}`} onClick={onRevoke}>
          <Trash2 aria-hidden="true" />
        </button>
      </td>
    </tr>
  );
}

export function TokensTable({
  tokens,
  empty,
  sort,
  order,
  onSort,
  onExtend,
  onRevoke,
}: {
  tokens: McpToken[];
  empty: string;
  sort: string;
  order: SortOrder;
  onSort: (key: string) => void;
  onExtend: (token: McpToken) => void;
  onRevoke: (token: McpToken) => void;
}) {
  if (tokens.length === 0) return <StateCard icon={<KeyRound aria-hidden="true" />} title={empty} />;
  return (
    <div className="table-scroll">
      <table className="data zebra compact">
        <thead>
          <tr>
            <SortHeader label="Name" sortKey="name" sort={sort} order={order} onSort={onSort} />
            <th scope="col">Scopes</th>
            <SortHeader label="Expires" sortKey="expires" sort={sort} order={order} onSort={onSort} />
            <SortHeader label="Last used" sortKey="lastUsed" sort={sort} order={order} onSort={onSort} />
            <SortHeader label="Created" sortKey="created" sort={sort} order={order} onSort={onSort} />
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {tokens.map((token) => (
            <TokenRow key={token.id} token={token} onExtend={() => onExtend(token)} onRevoke={() => onRevoke(token)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
